import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APP_CONFIG } from '@environments/environment';

export interface AuditLog {
    id: string;
    createdAt: string;
    actorUserId: string | null;
    actorUser?: {
        id: string;
        email: string;
        firstName?: string;
        lastName?: string;
        role?: string;
        status?: string;
    };
    actorType: string;
    subjectType: string;
    subjectId: string;
    action: string;
    outcome: string;
    ipAddress: string | null;
    userAgentId: string | null;
    userAgent?: {
        id: string;
        fullUserAgent?: string;
        parsedUserAgent?: string;
        browser?: string;
        os?: string;
        deviceType?: string | null;
    };
    requestId: string | null;
    correlationId: string | null;
    details: Record<string, any> | null;
}

@Injectable({
    providedIn: 'root',
})
export class AuditLogService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = APP_CONFIG.apiUrl;
    private readonly basePath = '/audit-logs';

    /**
     * Get audit logs by subject type and ID
     * @param subjectType - Type of subject (e.g., 'yearly_test_record')
     * @param subjectId - ID of the subject
     * @param limit - Maximum number of results (default: 50)
     * @param offset - Number of results to skip (default: 0)
     * @returns Observable of audit logs array
     */
    getAuditLogsBySubject(
        subjectType: string,
        subjectId: string,
        limit: number = 50,
        offset: number = 0,
    ): Observable<AuditLog[]> {
        const params = new HttpParams()
            .set('subjectType', subjectType)
            .set('subjectId', subjectId)
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        return this.http
            .get<{ data: { data: AuditLog[]; meta: any }; meta: any }>(`${this.apiUrl}${this.basePath}/by-subject`, {
                params,
                withCredentials: true,
            })
            .pipe(map((response) => response?.data?.data || []));
    }

    /**
     * Get all audit logs with pagination
     * @param limit - Maximum number of results (default: 100)
     * @param offset - Number of results to skip (default: 0)
     * @returns Promise of audit logs array
     */
    async getAllAuditLogs(limit: number = 100, offset: number = 0): Promise<AuditLog[]> {
        const params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString());

        try {
            const response = await this.http
                .get<{ data: { data: AuditLog[]; meta: any }; meta: any }>(`${this.apiUrl}${this.basePath}`, {
                    params,
                    withCredentials: true,
                })
                .toPromise();
            return response?.data?.data || [];
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            return [];
        }
    }
}

